import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Container, Graphics, Text, Sprite } from "@pixi/react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import * as PIXI from "pixi.js";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  closeBetSelectPopup,
  setBet,
  closeMenu,
} from "../../../../State/actions";
import {
  selectBetSelectPopupActive,
  selectBets,
  selectBet,
  selectBalance,
  selectCurrency,
} from "../../../../State/selectors";
import { PixiSettings } from "../../../../pixiSettings";
import { FONT_CONFIG } from "../../../../textureConfigs";
import { getUITexture } from "../../../../assets";
import BetSelectContent from "./BetSelectContent";
import anime from "animejs";
import { SOUND_NAME } from "../../../../sounds";
import { soundManager } from "../../../../SoundManager";

// Overlay settings — matching WarningPopup
const OVERLAY_BACKGROUND_ALPHA = 0.6;
const OVERLAY_BACKGROUND_COLOR = 0x090f41;
const OVERLAY_FADE_DURATION = 200;

// Popup animation settings
const POPUP_ANIM_DURATION = 250;

// Popup configuration
const BET_POPUP_CONFIG = {
  basePadding: 20,
  baseTitleFontSize: 28,
  baseContentWidth: 380,
  baseScrollbarOffset: 0.5,
  // The vertical frame has a built-in title bar area at the top (~13% of frame height)
  titleBarRatio: 0.13,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.75 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.6 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.85 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.85 },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 1.0 },
    },
  },
};

const BetSelectPopup = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const popupActive = useSelector(selectBetSelectPopupActive);
  const bets = useSelector(selectBets);
  const currentBet = useSelector(selectBet);
  const balance = useSelector(selectBalance);
  const currency = useSelector(selectCurrency);

  const { dimensions, device, orientation } = useDimensions();

  const popupContentRef = useRef(null);

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [selectedBet, setSelectedBet] = useState(currentBet);

  const isOpen = popupActive;

  const handleClose = useCallback(() => {
    if (isClosing) return;
    soundManager.play(SOUND_NAME.button_click);
    setIsClosing(true);
  }, [isClosing]);

  const handleBetSelect = useCallback(
    (betValue) => {
      soundManager.play(SOUND_NAME.button_click);
      setSelectedBet(betValue);
      dispatch(setBet(betValue));
      setIsClosing(true);
    },
    [dispatch],
  );

  // Reset selected bet when popup opens and close menu
  useEffect(() => {
    if (popupActive) {
      setSelectedBet(currentBet);
      dispatch(closeMenu());
    }
  }, [popupActive, currentBet, dispatch]);

  // Device config
  const deviceConfig = useMemo(() => {
    return (
      BET_POPUP_CONFIG.DEVICE_CONFIGS[device]?.[orientation] ||
      BET_POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP][
        PixiSettings.ORIENTATION.LANDSCAPE
      ]
    );
  }, [device, orientation]);

  const centerX = useMemo(() => dimensions.width / 2, [dimensions.width]);
  const centerY = useMemo(() => dimensions.height / 2, [dimensions.height]);

  // Texture
  const frameTexture = useMemo(() => getUITexture("popup_frame_vertical"), []);
  const closeTexture = useMemo(() => getUITexture("icon_close_menu"), []);
  // Button texture for aspect ratio calculation
  const buttonTexture = useMemo(() => getUITexture("popup_button"), []);
  const buttonAspectRatio = useMemo(
    () => (buttonTexture ? buttonTexture.width / buttonTexture.height : 2.0),
    [buttonTexture],
  );

  // Effective popup scale — clamped so the frame never exceeds 90% of viewport in either dimension.
  // All inner elements (padding, fonts, buttons) derive from this, so everything scales together.
  const popupScale = useMemo(() => {
    const deviceScale = deviceConfig.scaleMultiplier || 1.0;
    if (!frameTexture) return deviceScale;

    const idealContentWidth = BET_POPUP_CONFIG.baseContentWidth * deviceScale;
    const idealPadding = BET_POPUP_CONFIG.basePadding * deviceScale;
    const idealPanelWidth = idealContentWidth + idealPadding * 2;
    const idealFrameScale = idealPanelWidth / frameTexture.width;
    const idealFrameWidth = frameTexture.width * idealFrameScale;
    const idealFrameHeight = frameTexture.height * idealFrameScale;

    const maxWidth = dimensions.width * 0.8;
    const maxHeight = dimensions.height * 0.8;
    const widthRatio = idealFrameWidth > maxWidth ? maxWidth / idealFrameWidth : 1;
    const heightRatio = idealFrameHeight > maxHeight ? maxHeight / idealFrameHeight : 1;
    const clampRatio = Math.min(widthRatio, heightRatio);

    return deviceScale * clampRatio;
  }, [deviceConfig, frameTexture, dimensions.width, dimensions.height]);

  const padding = useMemo(
    () => BET_POPUP_CONFIG.basePadding * popupScale,
    [popupScale],
  );
  const titleFontSize = useMemo(
    () => Math.round(BET_POPUP_CONFIG.baseTitleFontSize * popupScale),
    [popupScale],
  );
  const contentWidth = useMemo(
    () => BET_POPUP_CONFIG.baseContentWidth * popupScale,
    [popupScale],
  );

  // Frame scaling — popupScale already accounts for viewport height
  const desiredPanelWidth = useMemo(
    () => contentWidth + padding * 2,
    [contentWidth, padding],
  );

  const frameScale = useMemo(() => {
    if (!frameTexture) return 1;
    return desiredPanelWidth / frameTexture.width;
  }, [frameTexture, desiredPanelWidth]);

  const frameSpriteWidth = useMemo(
    () => (frameTexture ? frameTexture.width * frameScale : 0),
    [frameTexture, frameScale],
  );
  const frameSpriteHeight = useMemo(
    () => (frameTexture ? frameTexture.height * frameScale : 0),
    [frameTexture, frameScale],
  );

  // Title bar area — the built-in lighter bar at the top of the vertical frame
  const titleBarHeight = useMemo(
    () => frameSpriteHeight * BET_POPUP_CONFIG.titleBarRatio,
    [frameSpriteHeight],
  );

  // Title style
  const titleText = useMemo(() => t("total_bet"), [t]);
  const titleStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: titleFontSize,
        fontWeight: "700",
        fill: 0xffbf11,
        align: "center",
      }),
    [titleFontSize],
  );

  // Viewport config — content area below the title bar
  const viewportConfig = useMemo(() => {
    const contentAreaTop = -frameSpriteHeight / 2 + titleBarHeight;
    const contentAreaBottom = frameSpriteHeight / 2 - padding;
    const maxWidth = frameSpriteWidth - padding * 2;
    const extraTopGap = padding * 0.75;
    const maxHeight =
      contentAreaBottom - contentAreaTop - padding * 2 - extraTopGap;
    return {
      maxWidth,
      maxHeight,
      x: -frameSpriteWidth / 2 + padding,
      y: contentAreaTop + padding * 2 + extraTopGap,
      titleFontSize,
      titleYOffset: 0,
      viewportHeight: maxHeight,
      scrollbarOffset: BET_POPUP_CONFIG.baseScrollbarOffset * popupScale,
    };
  }, [
    frameSpriteWidth,
    frameSpriteHeight,
    padding,
    titleBarHeight,
    titleFontSize,
    popupScale,
  ]);

  // Button config for 3-column grid
  const betButtonConfig = useMemo(() => {
    if (!viewportConfig)
      return {
        width: 100,
        height: 50,
        spacing: 10,
        rowSpacing: 10,
        columns: 3,
      };

    const columns = 3;
    const gapRatio = 0.075;
    const availableWidth = viewportConfig.maxWidth;
    const buttonWidth = availableWidth / (columns + (columns - 1) * gapRatio);
    const buttonHeight = buttonWidth / buttonAspectRatio;
    const spacing = buttonWidth * gapRatio;

    return {
      width: buttonWidth,
      height: buttonHeight,
      spacing,
      rowSpacing: spacing * 2.3,
      columns,
    };
  }, [viewportConfig, buttonAspectRatio]);

  // Overlay drawing
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  // Open animation: overlay first, then popup
  useEffect(() => {
    if (isOpen && !isClosing) {
      setIsVisible(true);
      // 1. Overlay fades in first
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          // 2. Then popup fades in
          if (popupContentRef.current) {
            popupContentRef.current.alpha = 0;
            popupContentRef.current.scale.set(0.85);
            anime({
              targets: popupContentRef.current,
              alpha: 1,
              duration: POPUP_ANIM_DURATION,
              easing: "easeOutQuad",
              complete: () => {
                setHasAnimated(true);
              },
            });
            anime({
              targets: popupContentRef.current.scale,
              x: 1,
              y: 1,
              duration: POPUP_ANIM_DURATION,
              easing: "easeOutQuad",
            });
          }
        },
      });
    } else if (!isOpen && !isClosing) {
      setIsVisible(false);
      setOverlayAlpha(0);
      setHasAnimated(false);
    }
  }, [isOpen, isClosing]);

  // Close animation: popup first, then overlay
  useEffect(() => {
    if (!isClosing) return;
    let overlayAnim = null;
    let popupAnim = null;
    if (popupContentRef.current) {
      // 1. Popup fades out first
      popupAnim = anime({
        targets: popupContentRef.current,
        alpha: 0,
        duration: POPUP_ANIM_DURATION,
        easing: "easeInQuad",
      });
      anime({
        targets: popupContentRef.current.scale,
        x: 0.85,
        y: 0.85,
        duration: POPUP_ANIM_DURATION,
        easing: "easeInQuad",
      });
    }
    // 2. Then overlay fades out
    overlayAnim = anime({
      targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_DURATION,
      delay: POPUP_ANIM_DURATION,
      easing: "easeInQuad",
      update: (anim) => {
        setOverlayAlpha(anim.animations[0].currentValue);
      },
      complete: () => {
        setOverlayAlpha(0);
        dispatch(closeBetSelectPopup());
        setIsClosing(false);
        setIsVisible(false);
        setHasAnimated(false);
      },
    });
    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (popupAnim) popupAnim.pause();
    };
  }, [isClosing, dispatch]);

  if (!isVisible && !isOpen) {
    return null;
  }

  return (
    <Container
      name="backport_bet_select_popup"
      zIndex={10000}
      sortableChildren={true}
    >
      {/* Overlay — click to close */}
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        cursor="pointer"
        pointerdown={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        zIndex={10000}
      />

      {/* Popup content */}
      <Container
        name="popup_content"
        ref={popupContentRef}
        zIndex={10001}
        x={centerX}
        y={centerY}
        alpha={hasAnimated ? 1 : 0}
        scale={hasAnimated ? 1 : 0.85}
      >
        {/* Frame background */}
        {frameTexture && (
          <Sprite
            texture={frameTexture}
            anchor={0.5}
            width={frameSpriteWidth}
            height={frameSpriteHeight}
          />
        )}

        {/* Close button — top right corner */}
        {closeTexture && (
          <Sprite
            texture={closeTexture}
            anchor={0.5}
            x={frameSpriteWidth / 2 - padding * 0.1}
            y={-frameSpriteHeight / 2 + padding * 0.1}
            width={titleFontSize * 1.8}
            height={titleFontSize * 1.8}
            eventMode="static"
            cursor="pointer"
            pointerdown={(e) => {
              e.stopPropagation();
              const btn = e.currentTarget;
              const origSize = titleFontSize * 1.8;
              soundManager.play(SOUND_NAME.button_click);
              anime.remove(btn);
              anime({
                targets: btn,
                width: [origSize * 0.85, origSize],
                height: [origSize * 0.85, origSize],
                duration: 200,
                easing: "easeOutQuad",
              });
              handleClose();
            }}
          />
        )}

        {/* Title — positioned in the frame's built-in title bar area */}
        <Text
          text={titleText}
          style={titleStyle}
          anchor={0.5}
          x={0}
          y={-frameSpriteHeight / 2 + titleBarHeight / 2 + titleFontSize * 1.25}
          resolution={2}
        />

        {/* Content viewport — below title bar */}
        <Container
          name="bet_content_viewport"
          x={viewportConfig.x}
          y={viewportConfig.y}
        >
          <BetSelectContent
            bets={bets}
            selectedBet={selectedBet}
            balance={balance}
            currency={currency}
            onBetSelect={handleBetSelect}
            viewportConfig={viewportConfig}
            gridColumns={betButtonConfig.columns}
            betButtonConfig={betButtonConfig}
            buttonSpacing={betButtonConfig.spacing}
            rowSpacing={betButtonConfig.rowSpacing}
          />
        </Container>
      </Container>
    </Container>
  );
};

export default BetSelectPopup;
